import { ChevronLeft, ShieldCheck, AlertTriangle, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/App";
import { toast } from "sonner";

const PROVIDER_LABEL = "AML-провайдер";

export const BUCKETS = [
  { key: "exchanges", labelRu: "Биржи (чистое)", labelEn: "Exchanges (clean)", kind: "good",
    signals: ["exchange_licensed", "exchange", "marketplace", "liquidity_pools"] },
  { key: "p2p", labelRu: "P2P / обмен", labelEn: "P2P / exchange", kind: "good",
    signals: ["p2p_exchange_licensed", "p2p_exchange", "payment", "wallet", "atm"] },
  { key: "scam", labelRu: "Скам / мошенники", labelEn: "Scam / fraud", kind: "bad",
    signals: ["scam", "exchange_fraudulent", "ransom", "illegal_service"] },
  { key: "darknet", labelRu: "Даркнет / микшер", labelEn: "Darknet / mixer", kind: "bad",
    signals: ["dark_market", "dark_service", "mixer"] },
  { key: "sanctions", labelRu: "Санкции", labelEn: "Sanctions", kind: "bad",
    signals: ["sanctions", "terrorism_financing", "seized_assets", "enforcement_action", "stolen_coins", "child_exploitation"] },
];

export const zoneOf = (percent, hardHit, decision) => {
  if (percent == null) return "unknown";
  if (decision) return ({pass: "low", review: "medium", block: "high"})[decision] || "unknown";
  if (hardHit || (percent != null && percent >= 70)) return "high";
  if (percent != null && percent > 30) return "medium";
  if (percent == null) return "unknown";
  return "low";
};

const ZONE = {
  low: { stroke: "#16a34a", text: "text-emerald-600" },
  medium: { stroke: "#d97706", text: "text-amber-600" },
  high: { stroke: "#dc2626", text: "text-red-600" },
  unknown: { stroke: "#9ca3af", text: "text-muted-foreground" },
};

const RiskGauge = ({ percent, zone }) => {
  const size = 132;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, percent ?? 0));
  const filled = (pct / 100) * c;
  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={ZONE[zone].stroke} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${filled} ${c - filled}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-bold ${ZONE[zone].text}`}>
          {percent == null ? "—" : `${percent}%`}
        </span>
        <span className="text-[10px] tracking-widest text-muted-foreground mt-1">РИСК</span>
      </div>
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
};

const AmlReportCard = ({ result, onBack, onCheckAnother }) => {
  const { t, i18n } = useTranslation();
  const { api, isTelegramWebApp } = useApp();
  const isRu = i18n.language?.toLowerCase().startsWith("ru");

  // window.print() в Telegram-webview не работает, поэтому отчёт открывается
  // отдельной серверной страницей во ВНЕШНЕМ браузере, где «Сохранить в PDF»
  // штатная. Авторизация — короткоживущим токеном в query (как у SSE).
  const openReport = async () => {
    if (!result?.uuid) {
      toast.error(t("aml.pdf_unavailable") || "Отчёт недоступен для этой проверки");
      return;
    }
    try {
      const { data } = await api.post("/events/token");
      const token = data?.token;
      if (!token) throw new Error("no token");
      const lang = isRu ? "ru" : "en";
      const url = `${window.location.origin}/api/aml/report/${encodeURIComponent(result.uuid)}?token=${encodeURIComponent(token)}&lang=${lang}`;
      const tg = window.Telegram?.WebApp;
      if (isTelegramWebApp && tg?.openLink) {
        tg.openLink(url);
      } else {
        const w = window.open(url, "_blank");
        if (!w) window.location.href = url;
      }
    } catch (e) {
      toast.error(t("aml.pdf_error") || "Не удалось открыть отчёт");
    }
  };

  const percent = result?.risk_percent;
  const hardHit = (result?.hard_signals || []).length > 0;
  const zone = zoneOf(percent, hardHit, result?.decision);
  const signals = result?.signals || {};

  const rows = (result?.categories || BUCKETS.map(b => ({key: b.key, label_ru: b.labelRu, label_en: b.labelEn,
    kind: b.kind, percent: Math.round(b.signals.reduce((sum, key) => sum + (Number(signals[key]) || 0), 0) * 10000) / 100})))
    .map(b => ({...b, value: b.percent, labelRu: b.label_ru, labelEn: b.label_en,
      barColor: b.kind === "bad" && b.percent > 0 ? "bg-red-500" : "bg-emerald-500"}));

  const verdict =
    zone === "low"
      ? { icon: <ShieldCheck className="w-5 h-5" />, title: t("aml.v_clean") || "Чистый кошелёк", sub: t("aml.v_clean_sub") || "Низкий риск · можно работать" }
      : zone === "medium"
      ? { icon: <AlertTriangle className="w-5 h-5" />, title: t("aml.v_medium") || "Средний риск", sub: t("aml.v_medium_sub") || "Проверьте источник средств" }
      : zone === "high"
      ? { icon: <XCircle className="w-5 h-5" />, title: t("aml.v_high") || "Высокий риск", sub: t("aml.v_high_sub") || "Работать не рекомендуется" }
      : { icon: <AlertTriangle className="w-5 h-5" />, title: t("aml.v_unknown") || "Не удалось оценить", sub: t("aml.v_unknown_sub") || "Провайдер не дал оценку" };

  return (
    <div id="aml-report-card" className="lp-glass p-5">
      <button onClick={onBack} className="mb-3 flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="w-4 h-4 mr-1" />
        {t("common.back") || "Назад"}
      </button>

      <h1 className="text-2xl font-bold mb-4">{t("aml.result_title") || "Результат проверки"}</h1>

      <RiskGauge percent={percent} zone={zone} />

      <div className="text-center mt-4">
        <div className={`inline-flex items-center gap-2 font-bold ${ZONE[zone].text}`}>
          {verdict.icon}
          <span>{verdict.title}</span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{verdict.sub}</p>
      </div>

      {result?.counterparty?.name && (
        <div className="mt-4 rounded-xl bg-[var(--lp-tile)] p-3 text-sm">
          <span className="text-muted-foreground">{t("aml.counterparty") || "Контрагент"}: </span>
          <span className="font-medium">
            {result.counterparty.name}
            {result.counterparty.type ? ` · ${result.counterparty.type}` : ""}
          </span>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {rows.map((r) => (
          <div key={r.key}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground">{isRu ? r.labelRu : r.labelEn}</span>
              <span className="font-bold">{r.value}%</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-[var(--lp-tile)] overflow-hidden">
              <div
                className={`h-full rounded-full ${r.barColor} transition-all`}
                style={{ width: `${r.value}%`, minWidth: r.value > 0 ? "2px" : undefined }}
              />
            </div>
          </div>
        ))}
      </div>

      <p className="mt-5 text-[11px] text-muted-foreground break-all">{result?.address}</p>
      <p className="mt-1 text-[11px] text-muted-foreground break-all">
        {(t("aml.date") || "Дата")}: {fmtDate(result?.created_at)} · ID: {result?.uuid || "—"}
      </p>

      {onCheckAnother && (
        <button
          onClick={onCheckAnother}
          className="lp-press mt-4 w-full rounded-xl border border-[rgb(var(--lp-edge)/var(--lp-edge-a))] py-2.5 text-sm font-semibold hover:bg-[var(--lp-tile)]"
        >
          {t("aml.check_another") || "Проверить другой"}
        </button>
      )}

      <p className="mt-4 text-center text-[11px] text-muted-foreground">
        {t("aml.source") || "Источник"}: {PROVIDER_LABEL} ·{" "}
        <button onClick={openReport} className="underline hover:text-foreground">
          {t("aml.save_pdf") || "сохранить отчёт PDF"}
        </button>
      </p>
    </div>
  );
};

export default AmlReportCard;
